import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Copy, Briefcase, ClipboardList, Trash2 } from "lucide-react";
import { deleteItem, deleteCandidate } from "@/lib/deleteItem";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, get, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STAFF } from "@/lib/nav";
import { EmptyState, SkeletonRows } from "@/components/app/Common";
import { ResumeViewer } from "@/pages/candidates/ResumeViewer";
import { ParsedEditor } from "@/pages/candidates/ParsedEditor";
import { ScreeningOverview } from "@/pages/candidates/ScreeningOverview";
import { AssessmentTab } from "@/pages/candidates/assessment/AssessmentTab";
import { ExperienceTab } from "@/pages/candidates/experience/ExperienceTab";
import { WorkSampleTab } from "@/pages/candidates/worksample/WorkSampleTab";
import { InterviewTab } from "@/pages/candidates/interview/InterviewTab";
import { useState } from "react";
import { WhatsNext } from "@/pages/candidates/overview/WhatsNext";
import { ProfileHeader, HeaderShell } from "@/pages/candidates/ProfileHeader";
import { FitOverview } from "@/pages/candidates/overview/FitOverview";
import { DecisionDialog } from "@/pages/candidates/overview/DecisionDialog";
import { OutcomeEmailDialog } from "@/pages/candidates/overview/OutcomeEmailDialog";
import { ActivityTab } from "@/pages/candidates/activity/ActivityTab";
import { TabBar } from "@/components/motion/TabBar";
import { CredentialsSection } from "@/pages/candidates/CredentialsSection";
import { KitButton } from "@/pages/candidates/KitButton";
import { CandidatePrivacy } from "@/pages/candidates/CandidatePrivacy";

const TABS = [["overview", "Overview"], ["resume", "Resume"], ["experience", "Experience review"], ["assessment", "Assessment"], ["interview", "Interview"], ["activity", "Notes & activity"], ["applications", "Applications"]];

function DuplicateBanner({ c, refresh }) {
  const act = async (action, msg) => {
    try { await api.post(`/candidates/${c.id}/duplicate`, { action }); toast.success(msg); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-[6px] border border-[#E8D7B0] bg-caution-tint px-5 py-3" data-testid="duplicate-banner">
      <Copy size={16} className="text-caution" />
      <p className="flex-1 text-sm text-ink">
        Possible duplicate of <Link to={`/app/candidates/${c.duplicate_of}/resume`} className="font-medium text-brand hover:underline">{c.duplicate_of_name || "an existing candidate"}</Link> ({c.duplicate_reasons.join(", ").toLowerCase()}).
      </p>
      <Button size="sm" variant="outline" onClick={() => act("merge", "Merged")} data-testid="profile-dup-merge">Merge</Button>
      <Button size="sm" variant="ghost" onClick={() => act("keep", "Kept both")} data-testid="profile-dup-keep">Keep both</Button>
      <Button size="sm" variant="ghost" onClick={() => act("skip", "Skipped")} data-testid="profile-dup-skip">Skip</Button>
    </div>
  );
}

function Applications({ c, canEdit, refresh }) {
  if (!c.applications.length) return <EmptyState icon={Briefcase} title="No applications" text="This candidate isn't linked to a job yet." />;
  const remove = (a) => deleteItem({ title: "Remove from this job?", entity: `${c.name} · ${a.job_title}`, confirmLabel: "Remove",
    body: "This removes the application with its invites, interview, notes and scores for this job. The candidate stays in your talent pool and other jobs.",
    url: `/applications/${a.id}`, success: `Removed from ${a.job_title}`, onDone: refresh });
  return (
    <div className="panel divide-y divide-line" data-testid="candidate-applications">
      {c.applications.map((a) => (
        <div key={a.id} className="space-y-2 px-5 py-4">
          <div className="flex items-center justify-between max-sm:flex-wrap max-sm:gap-2">
            <Link to={`/app/jobs/${a.job_id}/candidates`} className="font-medium text-brand hover:underline">{a.job_title}</Link>
            <span className="flex items-center gap-2 text-sm capitalize text-ink-secondary">{a.stage}
              {canEdit && <Button variant="ghost" size="sm" className="normal-case text-ink-muted hover:text-risk" onClick={() => remove(a)} data-testid={`remove-application-${a.id}`}>Remove from job</Button>}</span>
          </div>
          {a.knockout_answers?.map((k) => (
            <p key={k.question} className="text-sm text-ink-secondary"><ClipboardList size={12} className="mr-1 inline" />{k.question} <span className={k.passed ? "text-success" : "text-risk"}>{k.answer}</span></p>
          ))}
          {a.custom_answers?.filter((x) => x.answer).map((x) => <p key={x.id} className="text-sm"><span className="text-ink-muted">{x.question}</span> {x.answer}</p>)}
        </div>
      ))}
    </div>
  );
}

export default function CandidateProfile() {
  const { id, tab } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = STAFF.includes(user.role);
  const { data: c, isLoading, isError } = useQuery({
    queryKey: ["candidate", id], queryFn: () => get(`/candidates/${id}`),
    refetchInterval: (q) => (["queued", "processing"].includes(q.state.data?.parse_status) ? 3000 : false),
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["candidate", id] }); qc.invalidateQueries({ queryKey: ["candidates"] }); };
  const [appId, setAppId] = useState(null);
  const [deciding, setDeciding] = useState(false);
  const decide = (initial) => setDeciding(initial || true);
  const [outcome, setOutcome] = useState(false);
  if (isLoading) return <div className="space-y-6"><HeaderShell id={id} name={loc.state?.name} /><SkeletonRows rows={6} /></div>;
  if (isError) return <EmptyState testId="candidate-not-found" title="Candidate not found" text="They may have been merged or removed." />;
  const tabs = c && c.applications?.some((a) => a.work_sample_enabled) ? [...TABS.slice(0, 4), ["work-sample", "Work sample"], ...TABS.slice(4)] : TABS;
  if (c && !tabs.some(([k]) => k === tab)) return <Navigate to={`/app/candidates/${id}/overview`} replace />;
  const app = c.applications.find((a) => a.id === appId) || c.applications.find((a) => a.screening) || c.applications[0];
  const afterDecision = (res) => { qc.invalidateQueries({ queryKey: ["app-fit", app.id] }); refresh(); if (res.outcome_email) setOutcome(true); };

  return (
    <div className="space-y-6" data-testid="candidate-profile-page">
      <ProfileHeader c={c} canEdit={canEdit} refresh={refresh} app={app} onDecide={decide} />
      {canEdit && <div className="flex justify-end"><Button variant="ghost" size="sm" className="text-ink-muted hover:text-risk" data-testid={`delete-candidate-${c.id}`}
        onClick={() => deleteCandidate(c, () => { qc.removeQueries({ queryKey: ["candidate", id] }); qc.invalidateQueries({ queryKey: ["candidates"] }); nav("/app/candidates"); })}><Trash2 size={14} /> Delete candidate</Button></div>}
      {app && <WhatsNext appId={app.id} />}
      <CandidatePrivacy cid={c.id} canEdit={canEdit} />
      {c.applications.length > 1 && <select value={app?.id} onChange={(e) => setAppId(e.target.value)} className="h-9 w-fit rounded-lg border border-line bg-white px-2 text-sm" data-testid="profile-app-select">{c.applications.map((a) => <option key={a.id} value={a.id}>{a.job_title}</option>)}</select>}
      {c.duplicate_status === "pending" && canEdit && <DuplicateBanner c={c} refresh={refresh} />}
      {c.parse_status === "needs_attention" && (
        <p className="flex items-center gap-2 rounded-[6px] border border-[#E8C9C5] bg-risk-tint px-5 py-3 text-sm text-risk" data-testid="needs-attention-banner">
          <AlertTriangle size={16} /> We couldn't read this resume: {c.parse_error}. Enter the details manually below.
        </p>
      )}
      <TabBar tabs={tabs} active={tab} hrefFor={(k) => `/app/candidates/${id}/${k}`} testIdFor={(k) => `candidate-tab-${k}`} testId="candidate-tabs" />
      <div key={tab} className="tl-page">
        {tab === "resume" && (
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="lg:sticky lg:top-24 lg:self-start"><ResumeViewer candidate={c} /></div>
            {["queued", "processing"].includes(c.parse_status) ? <SkeletonRows rows={8} /> : <ParsedEditor candidate={c} canEdit={canEdit} />}
          </div>
        )}
        {tab === "overview" && <div className="space-y-8">{app?.screening && <FitOverview app={app} canEdit={canEdit} onDecide={() => decide()} onOutcome={() => setOutcome(true)} />}{app && <KitButton app={app} />}<CredentialsSection c={c} staff={canEdit} /><ScreeningOverview candidate={c} canEdit={canEdit} /></div>}
        {tab === "activity" && <ActivityTab c={c} />}
        {tab === "applications" && <Applications c={c} canEdit={canEdit} refresh={refresh} />}
        {tab === "assessment" && <AssessmentTab c={c} />}
        {tab === "experience" && <ExperienceTab c={c} />}
        {tab === "interview" && <InterviewTab c={c} appId={app?.id} onAppChange={setAppId} />}
        {tab === "work-sample" && c.applications.some((a) => a.work_sample_enabled) && <WorkSampleTab c={c} />}
      </div>
      {deciding && app && <DecisionDialog key={String(deciding)} initial={typeof deciding === "string" ? deciding : null} app={app} jobStatus={qc.getQueryData(["app-fit", app.id])?.job_status} name={c.display_name} open={!!deciding} onOpenChange={(o) => setDeciding(o)} onDecided={afterDecision} />}
      {outcome && app && <OutcomeEmailDialog appId={app.id} open={outcome} onOpenChange={setOutcome} onSent={refresh} />}
    </div>
  );
}
